import { useState, useRef, useCallback, useEffect } from 'react';
import { WIDGET_LAZY_LOAD_ROOT_MARGIN } from '../../constants';

const MAX_RETRIES = 3;
const RETRY_THROTTLE_MS = 2000;

/**
 * Manages data fetching, lazy loading, retries, and error state for a
 * single widget instance.
 *
 * @param {Object}   params
 * @param {string}   params.instanceUuid         - Unique widget instance identifier.
 * @param {Object}   params.scopeContext          - Scope information (e.g. contact_uuid).
 * @param {string}   params.dashboardUuid         - Parent dashboard UUID.
 * @param {Object}   params.activeFilters         - Currently applied global filters.
 * @param {boolean}  params.acceptsGlobalFilters  - Whether the widget consumes global filters.
 * @param {Function} params.fetchWidgetData       - Data adapter injected from context.
 *   Signature: (instanceUuid, scopeContext, dashboardContext, widgetContext?, options?) => Promise<{ data, meta }>
 *   `options.signal` carries an AbortSignal for cancellation on unmount.
 */
export const useWidgetData = ({
  instanceUuid,
  scopeContext,
  dashboardUuid,
  activeFilters,
  acceptsGlobalFilters,
  fetchWidgetData,
}) => {
  const [data, setData] = useState(null);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const containerRef = useRef(null);
  const fetchIdRef = useRef(0);
  const refetchControllerRef = useRef(null);
  const lastRetryAtRef = useRef(0);
  const loadingRef = useRef(loading);
  const errorRef = useRef(error);
  const retryCountRef = useRef(retryCount);
  /*
    The last request the lazy-load observer actually issued (CT-5896).

    The observer effect depends on `performFetch`, whose identity changes when
    `dashboardUuid` lands — the grid sets it from null after mount — so the
    effect tears down, re-observes an element that is already on screen and
    fires a second, byte-identical `widget/execute`. Six widgets meant twelve
    calls. Explicit `refetch` deliberately does not consult this.
  */
  const lastAutoRequestRef = useRef(null);

  const performFetch = useCallback(
    async (signal, { isRetry = false, widgetContext = null } = {}) => {
      const requestId = ++fetchIdRef.current;
      setLoading(true);
      setError(null);

      try {
        const dashboardContext = {
          dashboard_uuid: dashboardUuid,
          active_filters: acceptsGlobalFilters ? (activeFilters || null) : null,
        };

        const result = await fetchWidgetData(
          instanceUuid,
          scopeContext,
          dashboardContext,
          widgetContext,
          { signal },
        );

        if (requestId !== fetchIdRef.current || signal?.aborted) return;

        // The contract always returns 200. Datasource errors come in meta.error.
        if (result.meta?.error) {
          setError({ message: result.meta.error, isDatasourceError: true });
          setData(null);
          setMeta(result.meta);
        } else {
          setData(result.data);
          setMeta(result.meta);
          setRetryCount(0);
        }
      } catch (err) {
        if (err.name === 'AbortError') return;
        if (requestId === fetchIdRef.current) {
          setError(err);
          setData(null);
          setMeta(null);
          if (isRetry) {
            setRetryCount((prev) => prev + 1);
          }
        }
      } finally {
        if (requestId === fetchIdRef.current && !signal?.aborted) {
          setLoading(false);
        }
      }
    },
    [instanceUuid, scopeContext, dashboardUuid, activeFilters, acceptsGlobalFilters, fetchWidgetData],
  );

  useEffect(() => {
    setRetryCount(0);
  }, [scopeContext]);

  useEffect(() => {
    loadingRef.current = loading;
  }, [loading]);

  useEffect(() => {
    errorRef.current = error;
  }, [error]);

  useEffect(() => {
    retryCountRef.current = retryCount;
  }, [retryCount]);

  useEffect(() => {
    if (!instanceUuid || !scopeContext) return;

    const element = containerRef.current;
    if (!element) return;

    const controller = new AbortController();
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry?.isIntersecting) return;

        observer.disconnect();

        const requestKey = JSON.stringify([
          instanceUuid,
          dashboardUuid ?? null,
          acceptsGlobalFilters ? (activeFilters ?? null) : null,
          scopeContext ?? null,
        ]);
        if (lastAutoRequestRef.current === requestKey) return;
        lastAutoRequestRef.current = requestKey;

        performFetch(controller.signal, { isRetry: false });
      },
      {
        rootMargin: WIDGET_LAZY_LOAD_ROOT_MARGIN,
        threshold: 0,
      },
    );

    observer.observe(element);

    return () => {
      controller.abort();
      observer.disconnect();
    };
  }, [instanceUuid, scopeContext, dashboardUuid, activeFilters, acceptsGlobalFilters, performFetch]);

  const isEmpty = meta?.is_empty === true;
  const canRetry = retryCount < MAX_RETRIES && !loading;

  const refetch = useCallback(
    (widgetContext = null) => {
      if (loadingRef.current) return;
      const now = Date.now();
      if (now - lastRetryAtRef.current < RETRY_THROTTLE_MS) return;
      if (errorRef.current && retryCountRef.current >= MAX_RETRIES) return;

      lastRetryAtRef.current = now;
      refetchControllerRef.current?.abort();
      const controller = new AbortController();
      refetchControllerRef.current = controller;
      performFetch(controller.signal, { isRetry: !!errorRef.current, widgetContext });
    },
    [performFetch],
  );

  useEffect(() => {
    return () => {
      refetchControllerRef.current?.abort();
    };
  }, []);

  return {
    data,
    meta,
    loading,
    error,
    isEmpty,
    refetch,
    canRetry,
    containerRef,
  };
};
